import { useMemo } from 'react';

import { resolveIsOwner } from '@/utils/permissions';
import type { CatalogFilterKey, SixCatalogItem } from '@/types/Sixes';
import type { SortKey } from '@/types/SixCatalog.types';
import type { CatalogSortDirection } from '@/types/SixCatalogDTOs';

const RECENT_WINDOW_DAYS = 7;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const RECENT_WINDOW_MS =
  RECENT_WINDOW_DAYS *
  HOURS_PER_DAY *
  MINUTES_PER_HOUR *
  SECONDS_PER_MINUTE *
  MILLISECONDS_PER_SECOND;

type CatalogComparator = (firstSix: SixCatalogItem, secondSix: SixCatalogItem) => number;

const ascendingComparators: Record<SortKey, CatalogComparator> = {
  lastActivity: (firstSix, secondSix) =>
    firstSix.lastModified.getTime() - secondSix.lastModified.getTime(),
  name: (firstSix, secondSix) =>
    firstSix.name.localeCompare(secondSix.name, undefined, { sensitivity: 'base' }),
  members: (firstSix, secondSix) => firstSix.participantCount - secondSix.participantCount,
  files: (firstSix, secondSix) => firstSix.fileCount - secondSix.fileCount,
};

function invertComparator(comparator: CatalogComparator): CatalogComparator {
  return (firstSix, secondSix) => comparator(secondSix, firstSix);
}

function resolveComparator(
  sortKey: SortKey,
  sortDirection: CatalogSortDirection
): CatalogComparator {
  const ascendingComparator = ascendingComparators[sortKey];
  if (sortDirection === 'desc') {
    return invertComparator(ascendingComparator);
  }
  return ascendingComparator;
}

interface UseClientCatalogFilteringParams {
  sixes: SixCatalogItem[];
  searchValue: string;
  activeFilters: CatalogFilterKey[];
  currentUserId: string | undefined;
  selectedSortKey: SortKey;
  selectedSortDirection: CatalogSortDirection;
}

export function useClientCatalogFiltering({
  sixes,
  searchValue,
  activeFilters,
  currentUserId,
  selectedSortKey,
  selectedSortDirection,
}: UseClientCatalogFilteringParams): SixCatalogItem[] {
  return useMemo<SixCatalogItem[]>(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const now = Date.now();

    const passesFilter = (six: SixCatalogItem, filter: CatalogFilterKey): boolean => {
      switch (filter) {
        case 'favorites':
          return six.isFavorite;
        case 'mySixes':
          return resolveIsOwner(currentUserId, six.owners);
        case 'recent':
          return now - six.lastModified.getTime() <= RECENT_WINDOW_MS;
      }
    };

    const filtered = sixes.filter((six) => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        `${six.name} ${six.description}`.toLowerCase().includes(normalizedSearch);

      const matchesFilters = activeFilters.every((filter) => passesFilter(six, filter));

      // Temporary stopgap: local search + local filters with AND semantics.
      return matchesSearch && matchesFilters;
    });

    const comparator = resolveComparator(selectedSortKey, selectedSortDirection);

    return [...filtered].sort(comparator);
  }, [activeFilters, currentUserId, searchValue, selectedSortKey, selectedSortDirection, sixes]);
}
